import {
    FilterTarget,
    FilterValueWithMode,
    hasException,
} from "@impero-com/ui/domain";
import { Column } from "@tanstack/react-table";

/**
 * Creates a filter value getter specific to a filter schema type
 */
export function createFilterValueGetter<FS extends Record<string, unknown>>() {
    return <CID extends keyof FS, RD = unknown>(
        columnId: CID,
        column: Column<RD>
    ): FS[CID] => {
        return column.getFilterValue() as FS[CID];
    };
}

/**
 * Creates a helper that returns typed filter value and setter for a column.
 * Decouples filter components from directly accessing column.setFilterValue,
 * making them agnostic about the filter value storage structure.
 *
 * The `target` picks the filter's main value (the default) or its exception;
 * the setter leaves the other one untouched.
 *
 * @example
 * ```ts
 * const getFilterHelpers = createFilterHelpersGetter<MyColumnFilters>(column);
 *
 * // In each case of your switch:
 * const { value, setFilterValue } = getFilterHelpers(columnId);
 * // For a column offering exceptions, pass the target the editor is for:
 * const { value, setFilterValue } = getFilterHelpers(columnId, target);
 * ```
 */
export function createFilterHelpersGetter<
    FS extends Record<string, FilterValueWithMode<unknown> | null>,
>(column: Column<any, any>) {
    const getTypedFilterValue = createFilterValueGetter<FS>();

    return <K extends keyof FS>(id: K, target: FilterTarget = "main") => {
        type InnerValue = NonNullable<FS[K]>["value"];

        const filterValueWithMode = getTypedFilterValue(
            id,
            column
        ) as FilterValueWithMode<InnerValue> | null;

        const mainValue = filterValueWithMode?.value ?? null;
        const exception = hasException(filterValueWithMode)
            ? filterValueWithMode.exception
            : undefined;
        const value: InnerValue | null =
            (target === "main" ? mainValue : exception) ?? null;
        const mode = filterValueWithMode?.mode ?? "is";

        const setFilterValue = (newValue: InnerValue | null) => {
            const next =
                newValue === null ||
                (Array.isArray(newValue) && newValue.length === 0)
                    ? null
                    : newValue;
            if (target === "main") {
                column.setFilterValue({
                    value: next,
                    mode,
                    ...(exception !== undefined ? { exception } : {}),
                });
            } else {
                column.setFilterValue({
                    value: mainValue,
                    mode,
                    exception: next,
                });
            }
        };

        return { value, mode, setFilterValue };
    };
}
